import { useContext, useEffect, useState, useCallback, useRef } from "react";
// BrowserRouter
import { Link, useHistory } from "react-router-dom";
import "./App.css";
import GlobalContext from "GlobalContext";
import QlikObject from "components/QlikObject";
import ConnectionLostModal from "components/ConnectionLostModal";
import ControlSideBar from "components/ControlSideBar";
import BookmarkListMenu from "components/BookmarkListMenu";
import UserLoginLink from "components/UserLogin";
import UserLogoutLink from "components/UserLogout";
import Footer from "components/Footer";
import useAuthenticatedUser from "hooks/useAuthenticatedUser"; 
import GoogleAnalytics from "components/GoogleAnalytics";
import Routes from "./Routes";
import MainMenu from "./MainMenu";
import SideBar from "./SideBar";
import applyURLSelections from "utils/selectionsSupport";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from '@fortawesome/free-solid-svg-icons'; // Import the specific icon


function App() {
  const { qlik, app /*translations*/ } = useContext(GlobalContext);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [siderBarOpened, setSideBarOpened] = useState(false);
  const authUser = useAuthenticatedUser();
  const history = useHistory();
  // const [isAppReady, setAppReady] = useState(false);
  const [menuLayout, setMenuLayout] = useState({
    HeaderTitle: "DREAM Аналітика",
    FeedbackText: "Зворотній зв'язок",
    appDisclaimer:"На етапі пілотного проєкту результати аналізу не є офіційною статистикою і можуть бути не точними.",
    MenuItem1: "Показники",
    MenuItem2: "Проєкти / Об'єкти",
    MenuItem3: "Деталі проєктів",
    MenuItem4: "Фінансування",
    MenuItem5: "Деталі фінансування",
    MenuItem6: "Наповнення",
    MenuItem7: "Бюджетування",
    MenuItem8: "Відбори",
    MenuItem9: "Деталі фінансових програм",
    MenuItem10: "Фінансові програми, заявки",
    MenuItem11: "Фінансові програми",
    MenuItem12: "Інвестиційні проєкти",
    MenuItem13: "Географія інвестиційних проєктів",
    MenuItem14: "Контрактні процеси",
    MenuItem15: "Середньостроковий план пріоритетних публічних інвестицій / СПІ",
    MenuItem16: "Напрями інвестування",
    MenuItem17: "Секторальні портфелі держави",
    MenuItem18: "Єдиний проєктний портфель держави",
    MenuItem19: "Експертна оцінка проєктів і програм",
    MenuItem20: "Деталі публічних інвестиційних проєктів",
    MenuItemGroup1: "Управління публічними інвестиціями",
    MenuItemGroup2: "Проєкти відновлення",
    ConnectionLostTitle: "Підключення втрачено",
    ConnectionLostMessage:
      "Ваше підключення до Qlik Sense сервера втрачено, оновіть сторінку для продовження роботи.",
    ConnectionLostRefresh: "Оновити",
    Filters: "Всі фiльтри",
    Login: " ",
    isAppReady: false, // Add `isAppReady` to the initial state
    footerDisclaimer: "Цей проєкт створений за фінансової підтримки Програми допомоги з міжнародного розвитку від Уряду Великої Британії. Зміст є винятковою відповідальністю імплементуючого партнера Open Contracting Partnership; висловлені погляди не обов’язково відображають офіційну політику Уряду Великої Британії.",
  });

  const sidebarOverlayEl = useRef(null);

  const menuHandler = useCallback((e) => {
    e && e.preventDefault();
    if (document.body.classList.contains("sidebar-open")) {
      document.body.classList.remove("sidebar-open");
      document.body.classList.add("sidebar-closed");
    } else {
      document.body.classList.remove("sidebar-closed");
      document.body.classList.add("sidebar-open");
    } 
    document.body.classList.toggle("sidebar-collapse");
  }, []);

  const closeMenuHandler = useCallback(
    (e) => {
      if (sidebarOverlayEl.current) {
        console.log("sidebarOverlayEl :  " + sidebarOverlayEl.current);
        const overlayVisible =
          window.getComputedStyle(sidebarOverlayEl.current).display !== "none";
        if (overlayVisible) {
          // close menu when navigate (!)
          menuHandler(e);
        }
      }
    },
    [menuHandler]
  );

  useEffect(() => {
    // applyURLSelections here to be able to navigate bookmark's page
    (async () => {
      await applyURLSelections(app, history);
    })();    
    qlik.on("error", function (error) {
      // console.error(error.method);
      if (
        error.method === "OnSessionClosed" ||
        error.method === "OnSessionTimedOut" ||
        error.message === "ProxyError.OnSessionTimedOut" ||
        error.message === "ProxyError.OnSessionClosed"
      ) {
        setSessionClosed(true);
      }
    });
  }, [qlik, app, history]);

  useEffect(() => {
    let menuItems;
    const createGeneric = async () => {
      menuItems = app.createGenericObject(
        {
          HeaderTitle: {
            qStringExpression: "=$(T(mshp_Header))",
          },
          FeedbackText: {
            qStringExpression: "=$(T(mshp_FeedbackForm))",
          },
          RequestAnalyticsText: {
            qStringExpression: "=$(T(mshButtonRequestAnalytics))",
          },
          ExportChartFootnote: {
            qStringExpression: "=$(T(mshp_Header)) & '. ' & $(T(mshp_LastReloadTime)) & ' ' & date( last_load_time )",
          },
          appDisclaimer: {
            qStringExpression: "=$(T(mshp_appDisclaimer))",
          },
          footerDisclaimer: {
            qStringExpression: "=$(T(msh_footer_disclaimer))",
          },
          FeedbackURL: {
            qStringExpression: "=$(T(mshp_FeedbackForm_URL))",
          },
          KPIsAsOfDate: {
            qStringExpression: "=$(T(mshp_LastReloadTime)) & ' ' & ( last_load_time )",
          },
          MenuItem1: {
            qStringExpression: "=$(T(Показники))",
          },
          MenuItem2: {
            qStringExpression: "=$(T(Проекти))",
          },
          MenuItem3: {
            qStringExpression: "=$(T(ProjectDetails))",
          },
          MenuItem4: {
            qStringExpression: "=$(T(Фінансування))",
          },
          MenuItem5: {
            qStringExpression: "=$(T(ДеталіФінансування))",
          },
          MenuItem6: {
            qStringExpression: "=$(T(Наповнення))",
          },
          MenuItem7: {
            qStringExpression: "=$(T(Бюджетування))",
          },
          MenuItem8: {
            qStringExpression: "=$(T(Selections))",
          },
          MenuItem9: {
            qStringExpression: "=$(T(FinancialPrograms))",
          },
          MenuItem10: {
            qStringExpression: "=$(T(ФінансовіПрограмиЗаявки))",
          },
          MenuItem11: {
            qStringExpression: "=$(T(ФінансовіПрограми))",
          },
          MenuItem12: {
            qStringExpression: "=$(T(ІнвестиційніПроєкти))",
          },
          MenuItem13: {
            qStringExpression: "=$(T(ГеографіяІнвестиційнихПроєктів))",
          },
          MenuItem14: {
            qStringExpression: "=$(T(КонтрактніПроцеси))",
          },
          MenuItem15: {
            qStringExpression: "=$(T(mtpЛист))",
          },
          MenuItem16: {
            qStringExpression: "=$(T(spiНапрямиІнвестування))",
          },
          MenuItem17: {
            qStringExpression: "=$(T(spЛист))",
          },
          MenuItem18: {
            qStringExpression: "=$(T(eppЛист))",
          },
          MenuItem19: {
            qStringExpression: "=$(T(eoppЛист))",
          },
          MenuItem20: {
            qStringExpression: "=$(T(dpipЛист))",
          },
          MenuItemGroup1: {
            qStringExpression: "=$(T(msh_sidebar_pipelines_group))",
          },
          MenuItemGroup2: {
            qStringExpression: "=$(T(msh_sidebar_projects_group))",
          },
          ConnectionLostTitle: {
            qStringExpression: "=$(T(mshp_ConnectionLostTitle))",
          },
          ConnectionLostMessage: {
            qStringExpression: "=$(T(mshp_ConnectionLostMessage))",
          },
          ConnectionLostRefresh: {
            qStringExpression: "=$(T(mshp_ConnectionLostRefresh))",
          },
          Filters: {
            qStringExpression: "=$(T(mshp_Filters))",
          },
          Login: {
            qStringExpression: "=Coalesce($(T(ttlLogin)), ' ')",
          },
          exportText: {
            qStringExpression: "='Експорт даних...'",
          },
          exportCompletedText: {
            qStringExpression: "=$(T(mshp_export_complete))",
          },
          exportSelectObjectText: {
            qStringExpression: "=$(T(msh_export_selector_choose))",
          },
          exportNoObjectText: {
            qStringExpression: "=$(T(msh_export_selector_no_objects))",
          },
          closeBtnText: {
            qStringExpression: "=$(T(mshp_export_close))",
          },
          containerTabSelector: {
            qStringExpression: "=btnChoice",
          },
          containerTabSelectorProjects: {
            qStringExpression: "=btnChoice_2",
          },
          containerTabSelectorGeo: {
            qStringExpression: "=vGeoInvestBtn",
          },
          tourParamsNext: {
            qStringExpression: "=$(T(msh_tour_global_nextLabel))",
          },
          tourParamsPrev: {
            qStringExpression: "=$(T(msh_tour_global_prevLabel))",
          },
          tourParamsDone: {
            qStringExpression: "=$(T(msh_tour_global_doneLabel))",
          },
          tourParamsSkip: {
            qStringExpression: "=$(T(msh_tour_global_dontShowAgainLabel))",
          },
          tourGlobalNavbar: {
            qStringExpression: "=$(T(msh_tour_global_navbar))",
          },
          tourGlobalCurrentSelections: {
            qStringExpression: "=$(T(msh_tour_global_current_selections))",
          },
          tourGlobalCurrentSelectionsDefaultStatus: {
            qStringExpression: "=$(T(msh_tour_global_current_selections_status))",
          },
          tourGlobalAllFilters: {
            qStringExpression: "=$(T(msh_tour_global_filter_all))",
          },
          tourHomePage: {
            qStringExpression: "=$(T(msh_tour_home_page))",
          },
          tourHomeKPIRow: {
            qStringExpression: "=$(T(msh_tour_home_kpi_row))",
          },
          tourHomeKPISingle: {
            qStringExpression: "=$(T(msh_tour_home_kpi_single))",
          },
          tourHomeFiltersSidebar: {
            qStringExpression: "=$(T(msh_tour_home_filterpane_side))",
          },
          tourHomeBarchart: {
            qStringExpression: "=$(T(msh_tour_home_barchart))",
          },
          tourHomeBarchartSelection: {
            qStringExpression: "=$(T(msh_tour_home_barchart_selection))",
          },
          tourParamsOf: {
            qStringExpression: "=$(T(msh_tour_global_of))",
          },
          tourParamsTourStartButton: {
            qStringExpression: "=$(T(msh_tour_global_start_tour_button))",
          }

        },
          (reply) => {
            setMenuLayout((prevLayout) => ({
              ...prevLayout,
              ...reply,
              isAppReady: true
            }));
            // setAppReady(true);
          }
      );
    };
    createGeneric();
    return () => {
      if (menuItems && menuItems.qInfo)
        app.destroySessionObject(menuItems.qInfo.qId);
    };
  }, [app]);

  const sideBarOpenHandler = (e) => {
    e.preventDefault();
    setSideBarOpened(!siderBarOpened);
  };

  return (
    <>
      {sessionClosed && (
        <ConnectionLostModal
          title={menuLayout.ConnectionLostTitle}
          text={menuLayout.ConnectionLostMessage}
          refreshText={menuLayout.ConnectionLostRefresh}
        />
      )}
      {/* <Router> */}
        <nav className="main-header navbar navbar-expand navbar-white navbar-light">
          <ul className="navbar-nav">
            <li className="nav-item">
              <a
                className="nav-link pushmenu-btn"
                data-widget="pushmenu"
                href="/"
                role="button"
                onClick={menuHandler}
              >
                <i className="lui-icon lui-icon--menu"></i>
              </a>
            </li>
          </ul>
          <div className="form-inline">
            <h4 className="mb-0 prz-sale">{menuLayout.HeaderTitle}</h4>
          </div>

          <ul className="navbar-nav ml-auto d-flex align-items-center">

{/*             <li className="nav-item jumpappfeedback">
              <a
                className="nav-link nav-link-black"
                href={menuLayout.FeedbackURL}
                target="_blank"
                rel="noopener noreferrer"
                >
                🔗 {menuLayout.FeedbackText}
              </a>
            </li>
            <li className="nav-item jumpappfeedback-mobile">
              <a
                className="nav-link nav-link-black"
                href={menuLayout.FeedbackURL}
                target="_blank"
                rel="noopener noreferrer"
                >
                🔗
              </a>
            </li> */}

            <li className="nav-item lang-btns">
              <QlikObject id="7252e2f6-cf00-477a-b9a7-e17d82102ae5" height="29px" showLoader={false} />
            </li>
            <li className="nav-item w-lang-mobile lang-btns-mobile">
              <QlikObject id="d74900ec-bf2b-4d6d-a776-aba5bacb21bf" height="29px" showLoader={false} />
            </li>   
   
         
            <li className="nav-item">
              <a
                className={`nav-link tour-global-filterpane-dropdown ${
                  siderBarOpened ? "active filterpane-active" : "filterpane-inactive"
                }`}
                href="/"
                role="button"
                onClick={sideBarOpenHandler}
              >
                {siderBarOpened ? (
                  <FontAwesomeIcon icon={faXmark} className="lui-icon" />
                ) : (
                  <i className="lui-icon lui-icon--filterpane" />
                )}
                <span className={siderBarOpened ? "filters-txt-active" : "filters-txt"}>
                  {siderBarOpened ? " | " + menuLayout.closeBtnText : menuLayout.Filters}
                </span>
              </a>
            </li>

{/*             <li className="nav-item appHelp">
                <QlikObject
                  id="LTMED"
                  height="29px"
                  showLoader={false}
                />           
            </li> */}

            {!authUser.User || authUser.isAnonymous || sessionClosed ?
              <>
                <UserLoginLink loginText={menuLayout.Login} />
              </>
              :
              <>
                <BookmarkListMenu
                  app={app}
                  showBookmarkDetails={false}
                  user={authUser}
                />

                <UserLogoutLink user={authUser} />
              </>
              }

          </ul>
        </nav>

        <div className="main-header current-selections tour-global-current-selections">
          <QlikObject id="CurrentSelections" height="38px" showLoader={false} />
        </div>

        {/* sidebar-light-primary | sidebar-dark-secondary */}
        <aside className="main-sidebar sidebar-light-primary elevation-1">
{/*           <Link to="/" className="brand-link">
            <img
              src={`${process.env.PUBLIC_URL}logos/dream_logo_2.png`}
              alt="Logo"
              className="brand-image"
            />
          </Link> */}
          <a href={`https://${process.env.REACT_APP_QLIK_SERVER}${process.env.PUBLIC_URL || '/'}`}
             className="brand-link">
            <img
              src={`${process.env.PUBLIC_URL}logos/dream_logo_2.png`}
              alt="Logo"
              className="brand-image"
            />
          </a>

          <div className="sidebar tour-global-sidebar">
            <nav className="pt-2">
              <ul
                className="nav nav-pills nav-sidebar flex-column nav-child-indent"
                data-widget="treeview"
                role="menu"
                data-accordion="false"
              >
                <MainMenu
                  menuLayout={menuLayout}
                  closeMenuHandler={closeMenuHandler}
                />
              </ul>
            </nav>
              <div className="as-of-date-disclaimer">
                <p>
                {menuLayout.KPIsAsOfDate}
                </p>
              </div>
          </div>
        </aside>

        <div className="content-wrapper">
          <GoogleAnalytics id="X"> {/*G-B5WMEQ6H8V -for prod OR X -for test*/}
            <Routes menuLayout={menuLayout} />
          </GoogleAnalytics>
        </div>

        {/* Right sidebar */}
        <ControlSideBar opened={siderBarOpened}>
          {siderBarOpened && <SideBar />}
        </ControlSideBar>

        {/* <footer className="main-footer"></footer> */}
      {/* </Router> */}
      <Footer menuLayout={menuLayout} />
      <div id="sidebar-overlay" ref={sidebarOverlayEl} onClick={menuHandler} />
    </>
  );
}

export default App;
